import { useState } from "react";
import { NextPageContext } from "next";
import Layout from "../../components/Layout";
import { useGetVehiclesQuery } from "../../graphql/generated/graphql";
import {
  capitalizeAllWords,
  classNames,
  formatPrice,
} from "../../utils/formatters";
import CurrencyToggle from "../../components/CurrencyToggle";
import {
  CURRENCY_KEYS,
  VALUE_CALCULATOR_DISCLAIMER,
} from "../../utils/constants";
import Grid from "../../components/Grid";
import GoToWatchlistCard from "../../components/GoToWatchlistCard";
import CarValueForm from "../../components/CarValueForm";
import SellYourCarCTA from "../../components/SellYourCarCTA";
import { EmptyState } from "../../components/EmptyState";
import SEO from "../../components/SEO";
import { getData } from "../api/car-value/[slug]";

function getVariablesFromSlug(slug: string) {
  const [brandUnparsed, modelUnparsed, yearUnparsed] = slug.split("_");
  const brand = brandUnparsed?.replaceAll("-", " ");
  const model = modelUnparsed?.replaceAll("-", " ");
  const year = Number(yearUnparsed);

  return {
    brand,
    model,
    year,
  };
}

export default function CarValue({ data, variables, slug }) {
  const [currency, setCurrency] = useState(CURRENCY_KEYS.CRC);
  const title = capitalizeAllWords(
    `${variables?.brand} ${variables?.model} ${variables?.year}`
  );

  const {
    minPriceCRC,
    avgPriceCRC,
    maxPriceCRC,
    minPriceUSD,
    avgPriceUSD,
    maxPriceUSD,
    category,
  } = data || {};
  const min = currency === CURRENCY_KEYS.CRC ? minPriceCRC : minPriceUSD;
  const avg = currency === CURRENCY_KEYS.CRC ? avgPriceCRC : avgPriceUSD;
  const max = currency === CURRENCY_KEYS.CRC ? maxPriceCRC : maxPriceUSD;

  const {
    data: vehiclesData,
    loading: loadingVehicles,
    // error,
  } = useGetVehiclesQuery({
    skip: !slug || !category,
    variables: {
      limit: 9,
      searchText: variables?.model,
      filters: {
        category: [category],
        brand: [variables?.brand],
        yearMin: variables?.year - 2,
        yearMax: variables?.year + 2,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const { getVehicles } = vehiclesData || {};
  const { data: vehicles } = getVehicles || {};

  function onCurrencyClick(e) {
    e.preventDefault();

    setCurrency(e.target.id);
  }

  return (
    <>
      <SEO
        currentURL={`${process.env.NEXT_PUBLIC_ROOT_URL}/collection`}
        title={`Valor aproximado del ${title} en Costa Rica`}
        description={`Conocé el valor aproximado del ${title} o del auto que querás.`}
        siteTitle="Movi Autos"
      />
      <div className="flex max-w-7xl min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8 mx-auto">
        <div className="flex justify-center">
          <CarValueForm
            formData={{
              brand: {
                name: variables?.brand,
              },
              model: variables?.model,
              year: variables?.year,
            }}
            formClassName="w-full sm:flex items-center justify-center sm:gap-x-2 space-y-4 sm:space-y-0"
            submitButtonClassName="sm:mt-6"
          />
        </div>

        <div className="mt-2 max-w-2xl mx-auto">
          <p className="mt-1 text-xs text-gray-500 font-light">
            {VALUE_CALCULATOR_DISCLAIMER}
          </p>
        </div>
        <div className="mt-12">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl text-center">
            Precio promedio del
            <br />
            <span className="text-orange-600 pb-1.5 lg:pb-none px-4 lg:px-0 rounded-md leading-none">
              {title}
            </span>
            <br />
            en Costa Rica
          </h1>
        </div>

        <div
          className={classNames(
            " border-t border-b border-t-gray-200 border-b-gray-200 mt-12",
            data !== null ? "pb-6" : ""
          )}
        >
          {data === null ? (
            <EmptyState />
          ) : (
            <div>
              <dl className="mx-auto grid grid-cols-1 gap-px bg-gray-900/5 lg:grid-cols-3">
                <div className="flex flex-wrap items-baseline justify-center gap-x-4 bg-white px-4 py-6 text-center">
                  <dt className="text-sm font-medium leading-6 text-gray-500">
                    Mínimo
                  </dt>

                  <dd className="w-full flex-none text-2xl font-medium leading-10 tracking-tight text-gray-700">
                    {min
                      ? formatPrice(Math.round(min / 10) * 10, currency)
                      : "---"}
                  </dd>
                </div>
                <div className="flex flex-wrap items-baseline justify-center gap-x-4  bg-white px-4 py-6 text-center">
                  <dt className="text-md font-medium leading-6 text-gray-800">
                    Promedio
                  </dt>

                  <dd className="w-full flex-none text-4xl sm:text-5xl font-bold leading-10 tracking-tight text-orange-600">
                    {avg
                      ? formatPrice(Math.round(avg / 10) * 10, currency)
                      : "---"}
                  </dd>
                </div>
                <div className="flex flex-wrap items-baseline justify-center gap-x-4  bg-white px-4 py-6 text-center">
                  <dt className="text-sm font-medium leading-6 text-gray-500">
                    Máximo
                  </dt>

                  <dd className="w-full flex-none text-2xl font-medium leading-10 tracking-tight text-gray-700">
                    {max
                      ? formatPrice(Math.round(max / 10) * 10, currency)
                      : "---"}
                  </dd>
                </div>
              </dl>
              <div className="flex justify-center">
                <CurrencyToggle
                  currency={currency}
                  large
                  onClick={onCurrencyClick}
                />
              </div>
            </div>
          )}
        </div>
        <div className="mt-12">
          <Grid
            loading={loadingVehicles}
            items={vehicles}
            limit={9}
            title={
              <span className="">{title} a la venta en moviautos.com</span>
            }
            description={`* Mostrando ${capitalizeAllWords(
              `${title}`
            )} o de años cercanos.`}
            LastElement={() => (
              <GoToWatchlistCard
                pathname={`/autosusados?brand=${variables?.brand}&search=${variables?.model}`}
                icon="list"
                label="Ver todos"
              />
            )}
            EmptyStateComponent={() => (
              <div className="h-24 flex items-center justify-center">
                <p className="mt-1 font-light text-lg text-gray-500">
                  Sin resultados{" "}
                </p>
              </div>
            )}
          />
        </div>
        <SellYourCarCTA />
      </div>
    </>
  );
}

CarValue.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export const getServerSideProps = async (context: NextPageContext) => {
  const { query } = context;
  try {
    const { slug } = query;
    const variables = slug ? getVariablesFromSlug(slug as string) : null;
    const data = await getData(slug as string);

    return {
      props: {
        data,
        variables,
        slug,
      },
    };
  } catch (e) {
    console.log(e);
    return {
      redirect: {
        permanent: false,
        destination: "/valor-de-mi-auto",
      },
    };
  }
};
